import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import config from '../config';

let userPool = null;

const getUserPool = () => {
  if (!userPool) userPool = new CognitoUserPool({ UserPoolId: config.userPoolId, ClientId: config.clientId });
  return userPool;
};

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

// SRP login — routes to correct pool based on email vs username
export const login = async (identifier, password) => {
  const pool = getUserPool(); // single pool handles both via AliasAttributes
  // For username login, prepend schoolId from school context
  let cognitoUsername = identifier;
  if (!isEmail(identifier)) {
    const schoolId = localStorage.getItem('schoolId');
    if (!schoolId) throw new Error('School not selected — please select your school and try again');
    cognitoUsername = `${schoolId}_${identifier}`;
  }
  const user = new CognitoUser({ Username: cognitoUsername, Pool: pool });
  const authDetails = new AuthenticationDetails({ Username: cognitoUsername, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: async (session) => {
        const idToken = session.getIdToken().getJwtToken();
        const accessToken = session.getAccessToken().getJwtToken();
        const refreshToken = session.getRefreshToken().getToken();
        await setAuth({ idToken, accessToken, refreshToken });
        resolve({ success: true });
      },
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes) => {
        resolve({ newPasswordRequired: true, user, userAttributes });
      },
    });
  });
};

// Complete new password challenge on first login
export const completeNewPassword = (cognitoUser, newPassword) => {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: async (session) => {
        const idToken = session.getIdToken().getJwtToken();
        const accessToken = session.getAccessToken().getJwtToken();
        const refreshToken = session.getRefreshToken().getToken();
        await setAuth({ idToken, accessToken, refreshToken });
        resolve({ success: true });
      },
      onFailure: (err) => reject(err),
    });
  });
};

// API helpers
const getToken = () => localStorage.getItem('idToken');

const headers = () => ({
  'Content-Type': 'application/json',
  Authorization: getToken(),
});

let refreshPromise = null;
const refreshSession = () => {
  if (refreshPromise) return refreshPromise;
  refreshPromise = new Promise((resolve, reject) => {
    const user = getUserPool().getCurrentUser();
    if (!user) return reject(new Error('No user'));
    user.getSession((err, session) => {
      if (err) return reject(err);
      if (!session?.isValid()) return reject(new Error('Invalid session'));
      const idToken = session.getIdToken().getJwtToken();
      localStorage.setItem('idToken', idToken);
      resolve(idToken);
    });
  }).finally(() => { refreshPromise = null; });
  return refreshPromise;
};

const isTokenFromCorrectPool = () => {
  try {
    const token = getToken();
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Only reject if the token is from a completely different User Pool
    if (payload.iss && !payload.iss.includes(config.userPoolId)) return false;
    return true;
  } catch { return false; }
};

let loggingOut = false;
const forceLogout = () => {
  if (loggingOut) return;
  loggingOut = true;
  try { getUserPool().getCurrentUser()?.signOut(); } catch {}
  localStorage.clear();
  window.location.replace('/login');
};

const isTokenExpired = () => {
  try {
    const token = getToken();
    if (!token) return true;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now() - 60000; // expired or within 60s of expiry
  } catch { return true; }
};

const api = async (path, options = {}) => {
  if (loggingOut) throw new Error('Session expired');
  let token = getToken();
  if (!token || !isTokenFromCorrectPool()) { forceLogout(); throw new Error('Not authenticated'); }
  // Proactively refresh if token is expired or about to expire
  if (isTokenExpired()) {
    try {
      token = await refreshSession();
    } catch {
      forceLogout(); throw new Error('Session expired');
    }
  }
  let res = await fetch(`${config.apiUrl}${path}`, { ...options, headers: { 'Content-Type': 'application/json', Authorization: token } });
  if (res.status === 401) {
    try {
      token = await refreshSession();
      res = await fetch(`${config.apiUrl}${path}`, { ...options, headers: { 'Content-Type': 'application/json', Authorization: token } });
    } catch {
      forceLogout(); throw new Error('Session expired');
    }
    if (res.status === 401) { forceLogout(); throw new Error('Session expired'); }
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed (${res.status})`);
  }
  if (res.headers.get('content-type')?.includes('text/csv')) return res.text();
  return res.json();
};

// School Admins (Admin)
export const getSchoolAdmins = (schoolId) => api(`/school-admins${schoolId ? `?schoolId=${schoolId}` : ''}`);
export const createSchoolAdmin = (data) => api('/school-admins', { method: 'POST', body: JSON.stringify(data) });
export const resendSchoolAdminInvite = (id) => api(`/school-admins/${id}`, { method: 'PUT', body: JSON.stringify({ resendInvite: true }) });
export const deleteSchoolAdmin = (id) => api(`/school-admins/${id}`, { method: 'DELETE' });

// Classrooms
export const getClassrooms = () => api('/classrooms');
export const createClassroom = (data) => api('/classrooms', { method: 'POST', body: JSON.stringify(data) });
export const updateClassroom = (id, data) => api(`/classrooms/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteClassroom = (id) => api(`/classrooms/${id}`, { method: 'DELETE' });

// Schools (Admin)
export const getSchools = () => api('/schools');
export const createSchool = (data) => api('/schools', { method: 'POST', body: JSON.stringify(data) });
export const updateSchool = (id, data) => api(`/schools/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteSchool = (id) => api(`/schools/${id}`, { method: 'DELETE' });
export const getSchoolCurriculums = (schoolId) => api(`/schools/${schoolId}/curriculums`);
export const assignCurriculum = (schoolId, curriculumId) => api(`/schools/${schoolId}/curriculums/${curriculumId}`, { method: 'POST' });
export const unassignCurriculum = (schoolId, curriculumId) => api(`/schools/${schoolId}/curriculums/${curriculumId}`, { method: 'DELETE' });

export const checkUsernameAvailability = (username, schoolId) => api(`/students/username-check?username=${encodeURIComponent(username)}&schoolId=${encodeURIComponent(schoolId)}`);

// Students (Teacher)
export const getStudents = (schoolId) => api(`/students${schoolId ? `?schoolId=${schoolId}` : ''}`);
export const createStudent = (data) => api('/students', { method: 'POST', body: JSON.stringify(data) });
export const updateStudent = (id, data) => api(`/students/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const resendStudentInvite = (id) => api(`/students/${id}`, { method: 'PUT', body: JSON.stringify({ resendInvite: true }) });
export const deleteStudent = (id, schoolId) => api(`/students/${id}${schoolId ? `?schoolId=${schoolId}` : ''}`, { method: 'DELETE' });
export const createSingleStudent = (data) => api('/students', { method: 'POST', body: JSON.stringify(data) });
export const batchImportStudents = (students, schoolId, classroomId) => api('/students/batch', { method: 'POST', body: JSON.stringify({ students, schoolId, defaultClassroomId: classroomId }) });
export const batchImportTeachers = (teachers, schoolId, classroomId) => api('/teachers/batch', { method: 'POST', body: JSON.stringify({ teachers, schoolId, defaultClassroomId: classroomId }) });
export const batchImportSchoolAdmins = (schoolAdmins, defaultSchoolId) => api('/school-admins/batch', { method: 'POST', body: JSON.stringify({ schoolAdmins, defaultSchoolId }) });
export const bulkResetStudents = (userIds) => api('/students/bulk-reset', { method: 'POST', body: JSON.stringify({ userIds }) });
export const bulkResetTeachers = (userIds) => api('/teachers/bulk-reset', { method: 'POST', body: JSON.stringify({ userIds }) });
export const bulkResetSchoolAdmins = (userIds) => api('/school-admins/bulk-reset', { method: 'POST', body: JSON.stringify({ userIds }) });

// Teachers (Admin)
export const getTeachers = () => api('/teachers');
export const createTeacher = (data) => api('/teachers', { method: 'POST', body: JSON.stringify(data) });
export const updateTeacher = (id, data) => api(`/teachers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteTeacher = (id) => api(`/teachers/${id}`, { method: 'DELETE' });
export const resendTeacherInvite = (id) => api(`/teachers/${id}`, { method: 'PUT', body: JSON.stringify({ resendInvite: true }) });

// Curriculums
export const getCurriculums = () => api('/curriculums');
export const getCurriculum = (id) => api(`/curriculums/${id}`);
export const createCurriculum = (data) => api('/curriculums', { method: 'POST', body: JSON.stringify(data) });
export const updateCurriculum = (id, data) => api(`/curriculums/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteCurriculum = (id) => api(`/curriculums/${id}`, { method: 'DELETE' });
export const duplicateCurriculum = (id) => api(`/curriculums/${id}/duplicate`, { method: 'POST' });
export const enrollCurriculum = (id) => api(`/curriculums/${id}/enroll`, { method: 'POST' });

// PDF attachments
export const getPdfUploadUrl = (curriculumId) => api(`/curriculums/${curriculumId}/pdf?type=pdf`, { method: 'POST' });
export const getPdfDownloadUrl = (curriculumId, key) => api(`/curriculums/${curriculumId}/pdf?type=pdf&key=${encodeURIComponent(key)}`);
export const uploadPdfToS3 = async (presignedUrl, file) => {
  if (file.size > 50 * 1024 * 1024) throw new Error('PDF must be under 50MB');
  await fetch(presignedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': 'application/pdf' } });
};

// Thumbnail
export const getThumbnailUploadUrl = (curriculumId) => {
  const ext = 'jpg';
  return api(`/curriculums/${curriculumId}/pdf?type=thumbnail&ext=${ext}`, { method: 'POST' });
};
export const getThumbnailDownloadUrl = (_curriculumId, key) => {
  const domain = localStorage.getItem('cdnDomain');
  const cookies = JSON.parse(localStorage.getItem('cdnCookies') || 'null');
  if (domain && cookies && key) {
    const params = new URLSearchParams({
      Policy: cookies['CloudFront-Policy'],
      Signature: cookies['CloudFront-Signature'],
      'Key-Pair-Id': cookies['CloudFront-Key-Pair-Id'],
    });
    return Promise.resolve({ url: `https://${domain}/${key}?${params}` });
  }
  // Fallback to presigned URL if CDN cookies not available
  return api(`/curriculums/${_curriculumId}/pdf?type=thumbnail&key=${encodeURIComponent(key)}`);
};
export const uploadThumbnailToS3 = async (presignedUrl, file) => {
  if (file.size > 5 * 1024 * 1024) throw new Error('Thumbnail must be under 5MB');
  await fetch(presignedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
};

// Inline image upload
const getFileExt = (file) => {
  const name = file.name?.toLowerCase() || '';
  if (name.endsWith('.png')) return 'png';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'jpg';
  if (file.type === 'image/png') return 'png';
  return 'jpg';
};
export const getImageUploadUrl = (curriculumId, file) => {
  const ext = getFileExt(file);
  return api(`/curriculums/${curriculumId}/pdf?type=image&ext=${ext}`, { method: 'POST' });
};
export const uploadImageToS3 = async (presignedUrl, file) => {
  if (file.size > 10 * 1024 * 1024) throw new Error('Image must be under 10MB');
  const ext = getFileExt(file);
  if (!['png', 'jpg'].includes(ext)) throw new Error('Only PNG and JPG images are allowed');
  const contentType = ext === 'png' ? 'image/png' : 'image/jpeg';
  await fetch(presignedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } });
};

// Delete S3 object
export const deleteS3Object = (curriculumId, url) => {
  // Extract key from S3 URL
  const match = url.match(/curriculums\/[^?]+/);
  if (!match) return Promise.resolve();
  const key = match[0];
  return api(`/curriculums/${curriculumId}/pdf?key=${encodeURIComponent(key)}`, { method: 'DELETE' });
};

// CDN signed cookies for protected assets
let cdnDomain = null;
let cdnCookiesExpiry = 0;

const ensureCdnCookies = async () => {
  if (cdnDomain && Date.now() / 1000 < cdnCookiesExpiry - 300) return cdnDomain;
  try {
    const data = await api('/auth/cookies');
    cdnDomain = data.cdnDomain;
    cdnCookiesExpiry = data.expires;
    // Store cookies - these are sent automatically by the browser to the CDN domain
    // Since CDN is a different domain, we store them and append as query params instead
    localStorage.setItem('cdnDomain', data.cdnDomain);
    localStorage.setItem('cdnCookies', JSON.stringify(data.cookies));
    localStorage.setItem('cdnExpiry', data.expires);
    return cdnDomain;
  } catch { return null; }
};

export const getCdnUrl = (s3Url) => {
  if (!s3Url) return null;
  const cookies = JSON.parse(localStorage.getItem('cdnCookies') || 'null');
  const domain = localStorage.getItem('cdnDomain');
  if (!cookies || !domain) return s3Url;
  // Replace S3 URL with CDN URL and append signed cookie params
  let cdnUrl = s3Url;
  if (s3Url.includes('.s3.amazonaws.com/')) {
    const key = s3Url.split('.s3.amazonaws.com/')[1];
    cdnUrl = `https://${domain}/${key}`;
  } else if (s3Url.includes('.s3.us-east-1.amazonaws.com/')) {
    const key = s3Url.split('.s3.us-east-1.amazonaws.com/')[1];
    cdnUrl = `https://${domain}/${key}`;
  }
  const params = new URLSearchParams({
    Policy: cookies['CloudFront-Policy'],
    Signature: cookies['CloudFront-Signature'],
    'Key-Pair-Id': cookies['CloudFront-Key-Pair-Id'],
  });
  return `${cdnUrl}?${params}`;
};

export const refreshCdnCookies = () => ensureCdnCookies();

// School logo - uploaded to S3 via presigned URL
export const getLogoUploadUrl = (schoolId, file) => {
  const ext = file.name?.toLowerCase().endsWith('.png') ? 'png' : 'jpg';
  return api(`/schools/${schoolId}/logo?ext=${ext}`, { method: 'POST' });
};
export const uploadLogoToS3 = async (presignedUrl, file) => {
  if (file.size > 2 * 1024 * 1024) throw new Error('Logo must be under 2MB');
  const contentType = file.name?.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg';
  await fetch(presignedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } });
};
export const deleteSchoolLogo = (schoolId) => api(`/schools/${schoolId}/logo`, { method: 'DELETE' });
export const getLogoUrl = (logoKey) => {
  if (!logoKey) return null;
  const domain = localStorage.getItem('cdnDomain');
  const cookies = JSON.parse(localStorage.getItem('cdnCookies') || 'null');
  if (domain && cookies) {
    const params = new URLSearchParams({
      Policy: cookies['CloudFront-Policy'],
      Signature: cookies['CloudFront-Signature'],
      'Key-Pair-Id': cookies['CloudFront-Key-Pair-Id'],
    });
    return `https://${domain}/${logoKey}?${params}`;
  }
  return null;
};

export const getStudentUploadUrl = (curriculumId, file) => {
  const name = file.name?.toLowerCase() || '';
  let ext = 'jpg';
  if (name.endsWith('.png')) ext = 'png';
  else if (name.endsWith('.mp3')) ext = 'mp3';
  else if (name.endsWith('.ogg')) ext = 'ogg';
  return api(`/curriculums/${curriculumId}/pdf?type=student-upload&ext=${ext}`, { method: 'POST' });
};

// Submissions & Grades
export const saveProgress = (curriculumId, answers, currentActivity, currentLesson, visited) =>
  api(`/submissions/progress/${curriculumId}`, { method: 'PUT', body: JSON.stringify({ answers, currentActivity, currentLesson, visited }) });
export const getProgress = (curriculumId) => api(`/submissions/progress/${curriculumId}`);
export const getStudentProgress = (studentId, curriculumId) => api(`/submissions/progress/${curriculumId}?studentId=${studentId}`);
export const deleteStudentProgress = (studentId, curriculumId) => api(`/submissions/progress/${curriculumId}?studentId=${studentId}`, { method: 'DELETE' });
export const getAllProgress = () => api('/progress');
export const submitLesson = (curriculumId, lessonIndex, answers) =>
  api('/submissions', { method: 'POST', body: JSON.stringify({ curriculumId, lessonIndex, answers }) });
export const getGrades = () => api('/grades');
export const getStudentGrades = (studentId) => api(`/grades/${studentId}`);
export const unlockLesson = (studentId, curriculumId, lessonIndex) =>
  api('/submissions/unlock', { method: 'POST', body: JSON.stringify({ studentId, curriculumId, lessonIndex }) });
export const getSubmissionsByLesson = (curriculumId, lessonIndex) =>
  api(`/submissions/by-lesson?curriculumId=${curriculumId}&lessonIndex=${lessonIndex}`);
export const assignZeroGrade = (studentId, curriculumId, lessonIndex, score) =>
  api('/submissions/zero-grade', { method: 'POST', body: JSON.stringify({ studentId, curriculumId, lessonIndex, score }) });
export const submitManualGrades = (studentId, curriculumId, lessonIndex, manualGrades) =>
  api('/submissions/grade', { method: 'POST', body: JSON.stringify({ studentId, curriculumId, lessonIndex, manualGrades }) });

// Reports
export const downloadReport = () => api('/reports');

// Profile
export const getProfile = () => api('/profile');


// School lookup (public - no auth)
export const getSchoolByDomain = async (domain) => {
  const res = await fetch(`${config.apiUrl}/schools/lookup/${domain}`);
  if (!res.ok) throw new Error('School not found');
  return res.json();
};

export const getPublicSchools = async () => {
  // config.apiUrl may not be set yet if called immediately on mount — wait for it
  let apiUrl = config.apiUrl;
  if (!apiUrl) {
    await new Promise(r => setTimeout(r, 300));
    apiUrl = config.apiUrl;
  }
  const res = await fetch(`${apiUrl}/schools/public`);
  if (!res.ok) throw new Error('Failed to load schools');
  return res.json();
};

// Auth helpers
export const setAuth = async (tokens) => {
  localStorage.setItem('idToken', tokens.idToken);
  localStorage.setItem('refreshToken', tokens.refreshToken);
  const payload = JSON.parse(atob(tokens.idToken.split('.')[1]));

  // Fetch profile and CDN cookies in parallel
  const [profile] = await Promise.allSettled([
    api('/profile'),
    ensureCdnCookies(),
  ]);

  if (profile.status === 'fulfilled') {
    localStorage.setItem('user', JSON.stringify(profile.value));
  } else {
    localStorage.setItem('user', JSON.stringify({
      userId: payload.sub,
      email: payload.email,
      role: payload['custom:role'],
      schoolId: payload['custom:schoolId'],
      firstName: payload.given_name,
      lastName: payload.family_name,
    }));
  }
};

export const getUser = () => {
  const u = localStorage.getItem('user');
  return u ? JSON.parse(u) : null;
};

export const changePassword = (oldPassword, newPassword) => {
  return new Promise((resolve, reject) => {
    const user = getUserPool().getCurrentUser();
    if (!user) return reject(new Error('Not signed in'));
    user.getSession((err) => {
      if (err) return reject(err);
      user.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) return reject(err);
        resolve(result);
      });
    });
  });
};

export const forgotPassword = (email) => {
  const user = new CognitoUser({ Username: email, Pool: getUserPool() });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
      inputVerificationCode: () => resolve(),
    });
  });
};

export const confirmForgotPassword = (email, code, newPassword) => {
  const user = new CognitoUser({ Username: email, Pool: getUserPool() });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
};

export const logout = () => {
  const user = getUserPool().getCurrentUser();
  if (user) user.signOut();
  localStorage.clear();
  window.location.href = '/login';
};
